import {
  buildFabricAuthHeaders,
  createApiAxios,
  getFabricAuthTokens,
  getFabricEndpoint,
} from '@/utilities/axios/api-axios';
import {
  getPriorSession,
  getUserData,
} from '@/unified-modules/account/account-library';
import { APIServiceType } from '@/api/types';
import { CustomError } from '@/utilities/errors/CustomError';
import { ErrorType } from '@/utilities/errors/types';
import { setUserContextStatus, setUserSessionError } from '@/store/actions';
import { UserContextData } from '@/store/slices/userContext/types';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { PriorSession, User } from '@fanduel/cpe-um-bundle/account/contract';
import { useStore } from '@/store/useStore';

export const fetchUserContextApi = async (): Promise<UserContextData> => {
  const endpoint = getFabricEndpoint('userContext', 'accountSettings');
  const { baseURL, product } = useStore.getState().runtime;

  if (!endpoint || !product) {
    throw new CustomError(
      '[GW][GET_USER_CONTEXT] Missing required parameters',
      {
        type: ErrorType.API,
        reason: 'Missing endpoint or product',
        service: APIServiceType.USER_CONTEXT,
      },
    );
  }

  let userData: User | undefined = undefined;
  let priorSession: PriorSession | null | undefined = undefined;

  setUserContextStatus({ loading: true });

  try {
    userData = await getUserData();
    priorSession = await getPriorSession();
  } catch (error) {
    throw new CustomError('[GW][GET_USER_CONTEXT] Failed', {
      type: ErrorType.API,
      reason: 'Failed to fetch user account details',
      service: APIServiceType.USER_CONTEXT,
      useCase: DdEventCategory.Initialization,
      eventType: DdEventType.Error,
      stack: error instanceof Error ? error.stack : undefined,
      context: {
        error: error instanceof Error ? error.name : 'Unknown error',
      },
    });
  }

  try {
    const tokens = await getFabricAuthTokens('GET_USER_CONTEXT');

    const instance = createApiAxios();
    const response = await instance.get<UserContextData>(endpoint, {
      baseURL,
      params: {
        product,
      },
      headers: buildFabricAuthHeaders(tokens),
    });

    const responseData = response?.data ?? {};

    return {
      ...responseData,
      accountSettings: {
        ...responseData?.accountSettings,
        accountID: userData?.userId,
        username: userData?.username,
        firstName: userData?.firstName,
        lastName: userData?.lastName,
        countryCode: userData?.country,
        lastLoginDate: priorSession?.created ?? null,
      },
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.name : 'Unknown error';

    setUserContextStatus({ loading: false });
    setUserSessionError({
      type: 'FETCH_USER_CONTEXT',
      errorCode: null,
      details: {
        message: errorMessage,
        reason: 'fetch_failed',
      },
    });

    throw new CustomError('[GW][GET_USER_CONTEXT] Failed', {
      type: ErrorType.API,
      reason: 'Failed to fetch user context',
      service: APIServiceType.USER_CONTEXT,
      useCase: DdEventCategory.Initialization,
      eventType: DdEventType.Error,
      stack: error instanceof Error ? error.stack : undefined,
      context: {
        error: errorMessage,
      },
    });
  }
};

export default fetchUserContextApi;
